import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const GalaxyBackground = () => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const animationIdRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });

    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 1); // Black background
    mountRef.current.appendChild(renderer.domElement);

    // Store references
    sceneRef.current = scene;
    rendererRef.current = renderer;

    // Function to create a star mesh with varying sizes
    const createStar = () => {
      const size = Math.random() * 0.15 + 0.05; // Random size between 0.05 and 0.2
      const geometry = new THREE.SphereGeometry(size, 6, 6); // Small sphere for stars
      const material = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: Math.random() * 0.8 + 0.2 // Random opacity between 0.2 and 1.0
      });
      return new THREE.Mesh(geometry, material); // Create and return the star mesh
    };

    // Create multiple stars and add them to the scene
    const stars = [];
    for (let i = 0; i < 1500; i++) { // Increased star count for denser field
      const star = createStar(); // Create a star
      // Set random positions for stars in 3D space with more depth
      star.position.set(
        (Math.random() - 0.5) * 300, // Random x position (increased range)
        (Math.random() - 0.5) * 300, // Random y position (increased range)
        (Math.random() - 0.5) * 300  // Random z position (increased range)
      );
      scene.add(star); // Add star to the scene
      stars.push(star); // Keep reference for animation
    }

    // Add ambient light for better visibility
    const ambientLight = new THREE.AmbientLight(0x404040); // Soft white light
    scene.add(ambientLight); // Add ambient light to the scene

    // Add a directional light for highlights
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5); // Directional light
    scene.add(directionalLight); // Add directional light to the scene

    // Set camera position and orientation
    camera.position.set(0, 0, 100); // Position the camera back
    camera.lookAt(0, 0, 0); // Look at the center of the scene

    // Mouse movement tracking
    const handleMouseMove = (event) => {
      // Normalize mouse coordinates to -1 to 1 range
      mouseRef.current.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouseRef.current.y = -(event.clientY / window.innerHeight) * 2 + 1;
    };

    // Add mouse move listener
    window.addEventListener('mousemove', handleMouseMove);

    // Animation function to create a twinkling effect
    const animate = () => {
      // Slowly rotate the scene for a dynamic effect
      scene.rotation.y += 0.002;

      // Make camera follow mouse with smooth interpolation
      const targetX = mouseRef.current.x * 10;
      const targetY = mouseRef.current.y * 10;

      camera.position.x += (targetX - camera.position.x) * 0.05;
      camera.position.y += (targetY - camera.position.y) * 0.05;
      camera.lookAt(0, 0, 0);

      // Add subtle twinkling effect to stars
      stars.forEach((star, index) => {
        const time = Date.now() * 0.001;
        const baseOpacity = 0.3 + (star.position.z + 150) / 300 * 0.7; // Depth-based opacity
        star.material.opacity = baseOpacity + 0.3 * Math.sin(time * 2 + index * 0.1);
      });

      renderer.render(scene, camera); // Render the scene with the camera
      animationIdRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup function
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationIdRef.current) {
        window.cancelAnimationFrame(animationIdRef.current);
      }
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      // Clean up star geometries and materials
      stars.forEach(star => {
        star.geometry.dispose();
        star.material.dispose();
      });
      if (renderer) renderer.dispose();
    };
  }, []);

  return (
    <div 
      ref={mountRef} 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        pointerEvents: 'none'
      }}
    />
  );
};

export default GalaxyBackground;
